import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, useInView } from "framer-motion";
import { getGclid, getSource } from "@/utils/gclid";
import {
  Phone, Wrench, Thermometer, Drop, Lightning,
  CheckCircle, Clock, ShieldCheck, CurrencyInr, MapPin, Star,
  Warning, ArrowRight, PhoneCall, WhatsappLogo, Users,
  SealCheck, Leaf
} from "@phosphor-icons/react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PHONE = "9742119056";
const WHATSAPP_URL = `https://wa.me/91${PHONE}?text=Hi%2C%20I%20need%20AC%20repair%20in%20Bangalore`;

const SERVICES = [
  { name: "AC Inspection", price: "350", value: "inspection" },
  { name: "AC Jet Service", price: "850", value: "jet-service" },
  { name: "AC Cleaning", price: "1,049", value: "cleaning" },
  { name: "AC Foam Jet Service", price: "1,400", value: "foam-jet" },
  { name: "AC Gas Charging", price: "3,000", value: "gas-charging" },
];

const PROBLEMS = [
  { icon: Thermometer, q: "AC not cooling?", a: "Low gas, clogged filter, or compressor fault — we diagnose in 15 mins." },
  { icon: Drop, q: "Water dripping inside?", a: "Blocked drain line or frozen coils. Same-day fix guaranteed." },
  { icon: Warning, q: "Weird noise or vibration?", a: "Loose fan, motor issue, or debris. Our techs handle it all." },
  { icon: Lightning, q: "Electricity bill too high?", a: "Dirty AC draws 30% extra power. One service = months of savings." },
];

const REVIEWS = [
  { name: "Vikram S.", area: "JP Nagar", text: "Called at 11am. Technician arrived by 12. AC cooling perfectly now. No hidden charges, no drama. This is how service should be.", rating: 5 },
  { name: "Deepa R.", area: "Marathahalli", text: "My AC had a terrible smell. They did foam jet cleaning and it's like a brand new AC. ₹1400 well spent. Thank you team!", rating: 5 },
  { name: "Arjun P.", area: "Hebbal", text: "Gas charging + full service done in under 2 hours. Technician was polite, explained everything. Will recommend to everyone.", rating: 5 },
];

function Fade({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-30px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.45, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ─── HEADER ───
function Header() {
  return (
    <header data-testid="repair-header" className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-emerald-100">
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-14">
        <span className="font-['Plus_Jakarta_Sans'] font-extrabold text-emerald-900 text-base tracking-tight">
          HOME TECH REPAIR
        </span>
        <div className="flex items-center gap-3">
          <span className="hidden sm:block text-sm font-bold text-emerald-800 font-['IBM_Plex_Sans']">{PHONE}</span>
          <a
            href={`tel:${PHONE}`}
            data-testid="repair-header-call-btn"
            className="bg-emerald-700 text-white hover:bg-emerald-800 font-bold text-sm px-5 py-2.5 rounded-full transition-all flex items-center gap-2"
          >
            <Phone size={16} weight="bold" />
            Call Now
          </a>
        </div>
      </div>
    </header>
  );
}

// ─── HERO ───
function Hero() {
  return (
    <section data-testid="repair-hero-section" className="pt-14 bg-gradient-to-br from-emerald-50 via-white to-emerald-50/50 min-h-[90vh] flex items-center">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left */}
          <div>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <span className="inline-flex items-center gap-1.5 text-xs font-bold tracking-[0.2em] uppercase text-emerald-700 bg-emerald-100 px-4 py-1.5 rounded-full mb-6 font-['IBM_Plex_Sans']">
                <Clock size={14} weight="bold" /> 60-Min Response in Bangalore
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
              className="font-['Plus_Jakarta_Sans'] text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.08] text-emerald-950 mb-5"
            >
              Your AC Deserves
              <br />
              <span className="text-emerald-600">Expert Care.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}
              className="text-base sm:text-lg text-slate-600 leading-relaxed mb-6 max-w-lg font-['IBM_Plex_Sans']"
            >
              Certified technicians. Honest pricing. Guaranteed fix.
              <br />
              Get a <strong className="text-emerald-700">₹350 diagnostic check</strong> — pay only if you're happy.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-3 mb-8"
            >
              <a
                href={`tel:${PHONE}`}
                data-testid="repair-hero-call-btn"
                className="bg-emerald-700 text-white hover:bg-emerald-800 font-bold text-lg px-8 py-4 rounded-xl shadow-lg shadow-emerald-200 transition-all flex items-center justify-center gap-2"
              >
                <PhoneCall size={22} weight="bold" />
                Call {PHONE}
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank" rel="noopener noreferrer"
                data-testid="repair-hero-whatsapp-btn"
                className="bg-white text-emerald-800 border-2 border-emerald-200 hover:border-emerald-400 hover:bg-emerald-50 font-bold text-lg px-8 py-4 rounded-xl transition-all flex items-center justify-center gap-2"
              >
                <WhatsappLogo size={22} weight="bold" />
                WhatsApp Us
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.4 }}
              className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-500 font-['IBM_Plex_Sans']"
            >
              <span className="flex items-center gap-1.5"><CheckCircle size={16} weight="fill" className="text-emerald-600" /> Same-day repair</span>
              <span className="flex items-center gap-1.5"><CheckCircle size={16} weight="fill" className="text-emerald-600" /> No hidden charges</span>
              <span className="flex items-center gap-1.5"><CheckCircle size={16} weight="fill" className="text-emerald-600" /> Pay after service</span>
            </motion.div>
          </div>

          {/* Right: Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <BookingForm />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ─── TRUST STRIP ───
function TrustStrip() {
  const stats = [
    { icon: Users, label: "15,000+ ACs Serviced" },
    { icon: Star, label: "4.9/5 Customer Rating" },
    { icon: MapPin, label: "All Bangalore Areas" },
    { icon: Clock, label: "Limited Slots Today" },
  ];
  return (
    <section data-testid="repair-trust-strip" className="bg-emerald-800 text-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-emerald-700">
          {stats.map((s, i) => (
            <div key={i} className="py-4 px-4 flex items-center gap-2.5">
              <s.icon size={18} weight="bold" className="text-emerald-300 shrink-0" />
              <span className="text-xs sm:text-sm font-semibold font-['IBM_Plex_Sans']">{s.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── BOOKING FORM ───
function BookingForm() {
  const [form, setForm] = useState({ name: "", phone: "", area: "", service: "" });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.area || !form.service) {
      toast.error("Please fill all fields");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/leads`, { ...form, gclid: getGclid(), source: getSource() });
      if (res.data.success) {
        navigate(`/thank-you?name=${encodeURIComponent(form.name)}&service=ac-repair`);
      }
    } catch {
      toast.error("Something went wrong. Please call us directly.");
    }
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} data-testid="repair-booking-form" className="bg-white p-6 sm:p-8 rounded-2xl shadow-xl shadow-emerald-100 border border-emerald-100">
      <h3 className="font-['Plus_Jakarta_Sans'] text-xl font-bold text-emerald-900 mb-1">Book AC Repair</h3>
      <p className="text-xs text-slate-500 font-['IBM_Plex_Sans'] mb-5">We call you back in 5-10 minutes</p>
      <div className="space-y-3">
        <input
          data-testid="repair-form-name"
          type="text" placeholder="Your Name"
          value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="w-full bg-emerald-50/50 border border-emerald-200 rounded-lg p-3 text-sm font-['IBM_Plex_Sans'] focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none transition-all"
        />
        <input
          data-testid="repair-form-phone"
          type="tel" placeholder="Phone Number"
          value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
          className="w-full bg-emerald-50/50 border border-emerald-200 rounded-lg p-3 text-sm font-['IBM_Plex_Sans'] focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none transition-all"
        />
        <input
          data-testid="repair-form-area"
          type="text" placeholder="Your Area in Bangalore"
          value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })}
          className="w-full bg-emerald-50/50 border border-emerald-200 rounded-lg p-3 text-sm font-['IBM_Plex_Sans'] focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none transition-all"
        />
        <Select value={form.service} onValueChange={(val) => setForm({ ...form, service: val })}>
          <SelectTrigger
            data-testid="repair-form-service-select"
            className="w-full bg-emerald-50/50 border border-emerald-200 rounded-lg p-3 h-auto text-sm font-['IBM_Plex_Sans'] focus:ring-2 focus:ring-emerald-100"
          >
            <SelectValue placeholder="Select Service" />
          </SelectTrigger>
          <SelectContent>
            {SERVICES.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.name} — ₹{s.price}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <button
        type="submit" disabled={loading}
        data-testid="repair-booking-submit"
        className="w-full mt-5 bg-emerald-700 text-white hover:bg-emerald-800 font-bold text-sm px-6 py-4 rounded-xl shadow-lg shadow-emerald-200 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {loading ? "Booking..." : "Book Repair Now"}
        {!loading && <ArrowRight size={16} weight="bold" />}
      </button>
    </form>
  );
}

// ─── PROBLEM → SOLUTION ───
function ProblemSection() {
  return (
    <section data-testid="repair-problem-section" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <Fade>
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-emerald-600 mb-4 block font-['IBM_Plex_Sans']">
            Common AC Problems
          </span>
          <h2 className="font-['Plus_Jakarta_Sans'] text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-emerald-950 mb-12">
            What's Wrong With Your AC?
          </h2>
        </Fade>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {PROBLEMS.map((p, i) => (
            <Fade key={i} delay={i * 0.08}>
              <div
                data-testid={`repair-problem-card-${i}`}
                className="bg-emerald-50/60 border border-emerald-100 rounded-2xl p-6 flex gap-4 hover:border-emerald-300 hover:shadow-md transition-all"
              >
                <div className="shrink-0 w-11 h-11 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <p.icon size={22} weight="duotone" className="text-emerald-700" />
                </div>
                <div>
                  <h4 className="font-['Plus_Jakarta_Sans'] text-base font-bold text-emerald-900 mb-1">{p.q}</h4>
                  <p className="text-sm text-slate-500 font-['IBM_Plex_Sans'] leading-relaxed">{p.a}</p>
                </div>
              </div>
            </Fade>
          ))}
        </div>
        <Fade delay={0.3}>
          <div className="mt-10 flex flex-col sm:flex-row items-center gap-4">
            <a
              href={`tel:${PHONE}`}
              data-testid="repair-problem-call-btn"
              className="bg-emerald-700 text-white hover:bg-emerald-800 font-bold text-base px-8 py-4 rounded-xl shadow-lg shadow-emerald-200 transition-all flex items-center gap-2"
            >
              <Phone size={20} weight="bold" /> Describe Your Problem — {PHONE}
            </a>
            <a
              href={WHATSAPP_URL}
              target="_blank" rel="noopener noreferrer"
              data-testid="repair-problem-whatsapp-btn"
              className="bg-white text-emerald-800 border-2 border-emerald-200 hover:border-emerald-400 font-bold text-base px-8 py-4 rounded-xl transition-all flex items-center gap-2"
            >
              <WhatsappLogo size={20} weight="bold" /> WhatsApp Us
            </a>
          </div>
        </Fade>
      </div>
    </section>
  );
}

// ─── SERVICES & PRICING ───
function ServicesSection() {
  return (
    <section data-testid="repair-services-section" className="bg-emerald-50/50 py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <Fade>
          <div className="text-center mb-12">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-emerald-600 mb-4 block font-['IBM_Plex_Sans']">
              Honest Pricing
            </span>
            <h2 className="font-['Plus_Jakarta_Sans'] text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-emerald-950 mb-3">
              AC Repair & Service Rates
            </h2>
            <p className="text-slate-500 font-['IBM_Plex_Sans'] max-w-md mx-auto">
              No hidden fees. You pay only after the job is done.
            </p>
          </div>
        </Fade>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-4xl mx-auto">
          {SERVICES.map((s, i) => {
            const isInspection = s.value === "inspection";
            return (
              <Fade key={s.value} delay={i * 0.06}>
                <div
                  data-testid={`repair-service-card-${s.value}`}
                  className={`relative rounded-2xl p-6 flex flex-col transition-all hover:-translate-y-1 ${
                    isInspection
                      ? "bg-emerald-700 text-white shadow-xl shadow-emerald-200 ring-2 ring-emerald-500"
                      : "bg-white border border-emerald-100 shadow-sm hover:shadow-md"
                  }`}
                >
                  {isInspection && (
                    <span className="absolute -top-3 left-5 bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full">
                      Start Here
                    </span>
                  )}
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${isInspection ? "bg-white/20" : "bg-emerald-50"}`}>
                    <Wrench size={18} weight="duotone" className={isInspection ? "text-white" : "text-emerald-700"} />
                  </div>
                  <h3 className={`font-['Plus_Jakarta_Sans'] text-sm font-bold mb-1 ${isInspection ? "text-white" : "text-emerald-900"}`}>
                    {s.name}
                  </h3>
                  <span className={`font-['Plus_Jakarta_Sans'] text-2xl font-extrabold mt-auto pt-3 ${isInspection ? "text-emerald-200" : "text-emerald-800"}`}>
                    ₹{s.price}
                  </span>
                </div>
              </Fade>
            );
          })}
        </div>

        <Fade delay={0.3}>
          <p className="text-center text-sm text-slate-500 font-['IBM_Plex_Sans'] mt-8">
            Not sure what you need? Start with a <strong className="text-emerald-700">₹350 check-up</strong> — our tech will find the exact issue.
          </p>
        </Fade>
      </div>
    </section>
  );
}

// ─── TESTIMONIALS ───
function TestimonialsSection() {
  return (
    <section data-testid="repair-testimonials-section" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <Fade>
          <div className="text-center mb-12">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-emerald-600 mb-4 block font-['IBM_Plex_Sans']">
              Verified Reviews
            </span>
            <h2 className="font-['Plus_Jakarta_Sans'] text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-emerald-950">
              Bangalore Locals Trust Us
            </h2>
          </div>
        </Fade>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {REVIEWS.map((r, i) => (
            <Fade key={i} delay={i * 0.1}>
              <div
                data-testid={`repair-testimonial-card-${i}`}
                className="bg-emerald-50/50 border border-emerald-100 rounded-2xl p-6 h-full flex flex-col hover:shadow-md transition-shadow"
              >
                <div className="flex gap-0.5 mb-3">
                  {[...Array(r.rating)].map((_, j) => (
                    <Star key={j} size={15} weight="fill" className="text-emerald-500" />
                  ))}
                </div>
                <p className="text-sm text-slate-600 font-['IBM_Plex_Sans'] leading-relaxed mb-5 flex-1">
                  "{r.text}"
                </p>
                <div className="flex items-center gap-3 pt-4 border-t border-emerald-100">
                  <div className="w-8 h-8 bg-emerald-700 text-white rounded-full flex items-center justify-center text-xs font-bold">
                    {r.name[0]}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-emerald-900 font-['Plus_Jakarta_Sans']">{r.name}</p>
                    <p className="text-xs text-slate-400">{r.area}, Bangalore</p>
                  </div>
                  <SealCheck size={18} weight="fill" className="text-emerald-500 ml-auto" />
                </div>
              </div>
            </Fade>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── FINAL CTA ───
function FinalCTA() {
  return (
    <section data-testid="repair-final-cta-section" className="bg-emerald-800 py-20 md:py-28 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-emerald-700 via-emerald-800 to-emerald-900" />
      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 text-center">
        <Fade>
          <Leaf size={40} weight="duotone" className="text-emerald-400 mx-auto mb-4" />
          <h2 className="font-['Plus_Jakarta_Sans'] text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white mb-3 leading-tight">
            Cool Air is One Call Away.
          </h2>
          <p className="text-emerald-200/80 font-['IBM_Plex_Sans'] text-base mb-8 max-w-md mx-auto">
            One call. One visit. Problem solved. Diagnostic starts at just ₹350.
          </p>
          <a
            href={`tel:${PHONE}`}
            data-testid="repair-final-cta-phone"
            className="font-['Plus_Jakarta_Sans'] text-5xl sm:text-6xl lg:text-7xl font-extrabold text-white tracking-tighter inline-block mb-8 hover:text-emerald-300 transition-colors cursor-pointer"
          >
            {PHONE}
          </a>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`tel:${PHONE}`}
              data-testid="repair-final-call-btn"
              className="bg-white text-emerald-800 hover:bg-emerald-50 font-bold text-lg px-10 py-5 rounded-xl shadow-lg transition-all inline-flex items-center justify-center gap-2"
            >
              <PhoneCall size={22} weight="bold" /> Call Now
            </a>
            <a
              href={WHATSAPP_URL}
              target="_blank" rel="noopener noreferrer"
              data-testid="repair-final-whatsapp-btn"
              className="border-2 border-white/30 text-white hover:bg-white/10 font-bold text-lg px-10 py-5 rounded-xl transition-all inline-flex items-center justify-center gap-2"
            >
              <WhatsappLogo size={22} weight="bold" /> WhatsApp
            </a>
          </div>
        </Fade>
      </div>
    </section>
  );
}

// ─── FOOTER ───
function RepairFooter() {
  return (
    <footer data-testid="repair-footer" className="bg-emerald-900 py-8">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <span className="font-['Plus_Jakarta_Sans'] font-extrabold text-white text-base block">HOME TECH REPAIR</span>
            <span className="text-emerald-400/60 text-sm font-['IBM_Plex_Sans']">AC Repair & Service — Bangalore</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-emerald-400/60 font-['IBM_Plex_Sans']">
            <a href={`tel:${PHONE}`} className="hover:text-white transition-colors flex items-center gap-1.5">
              <Phone size={14} weight="bold" /> {PHONE}
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors flex items-center gap-1.5">
              <WhatsappLogo size={14} weight="bold" /> WhatsApp
            </a>
          </div>
        </div>
        <div className="mt-6 pt-6 border-t border-white/5 text-center text-xs text-emerald-400/30 font-['IBM_Plex_Sans']">
          Home Tech Repair Bangalore. All rights reserved.
        </div>
      </div>
    </footer>
  );
}

// ─── MAIN PAGE ───
export default function RepairPage() {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <Hero />
      <TrustStrip />
      <ProblemSection />
      <ServicesSection />
      <TestimonialsSection />
      <FinalCTA />
      <RepairFooter />
    </div>
  );
}
